import { Link } from 'react-router-dom';

import { FormatNumber } from 'utils/helpers';

import { GraficoDePontosPorFase, Indicador } from '../abas/RealizacaoDestaque';
import CabecalhoDaSecao, { ACAO_DO_CABECALHO, SetaDaAcao } from './CabecalhoDaSecao';

/** Rota da página de Realização Destaque do participante. */
export const ROTA_DA_REALIZACAO_DESTAQUE = '/realizacao-destaque';

export interface ContadoresDoCicloAberto {
  cadastradas: number;
  participacoes: number;
  aguardando: number;
}

type ResumoRealizacoesProps =
  | {
      aberto: true;
      /** De `find/entregas` (`contadoresDoSemestre`); ausentes enquanto carregam. */
      contadores?: ContadoresDoCicloAberto;
    }
  | {
      aberto: false;
      /** Do resumo do ciclo; ausente enquanto ele recarrega. */
      totalDoCiclo?: number;
      /** Os créditos de RD do ciclo (os mesmos da aba); ausentes enquanto carregam. */
      itens?: any[];
    };

const CHIP = 'rounded-jj-pill bg-brand-white px-[13px] py-1.5 font-display text-xs font-bold text-brand-blue-dark';

function CicloAberto({ contadores }: { contadores?: ContadoresDoCicloAberto }) {
  if (!contadores)
    return (
      <span
        role="status"
        aria-label="Carregando realizações"
        className="block h-[29px] w-72 animate-pulse rounded-jj-pill bg-white/50"
      />
    );

  return (
    <ul aria-label="Suas realizações no ciclo" className="m-0 flex list-none flex-wrap gap-2 p-0">
      <li className={CHIP}>{contadores.cadastradas} cadastradas por você</li>
      <li className={CHIP}>{contadores.participacoes} participações</li>
      <li className={CHIP}>{contadores.aguardando} aguardando o Nível 2</li>
    </ul>
  );
}

function CicloFechado({ totalDoCiclo, itens }: { totalDoCiclo?: number; itens?: any[] }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] items-stretch gap-6">
      <div className="flex flex-wrap content-center gap-3.5">
        {totalDoCiclo !== undefined && <Indicador rotulo="Total do ciclo" valor={FormatNumber(totalDoCiclo, false)} />}
        {itens && <Indicador rotulo="Realizações" valor={itens.length} />}
      </div>
      {itens && itens.length > 0 && (
        <div className="flex min-w-0 flex-col gap-3 rounded-xl bg-brand-white px-5 pb-3.5 pt-4">
          <span className="font-display text-sm font-bold text-brand-blue-dark">Pontos por fase</span>
          <GraficoDePontosPorFase itens={itens} rotulo="Pontos de Realização Destaque por fase" className="h-[140px]" />
        </div>
      )}
    </div>
  );
}

/**
 * AJ2-34: ciclo fechado com o total, os pontos por fase e a quantidade de realizações; ciclo aberto só com os chips,
 * sem pontos (a pontuação prévia depende da regra dos pontos prévios, fora desta entrega).
 */
export default function ResumoRealizacoes(props: ResumoRealizacoesProps) {
  return (
    <section aria-labelledby="titulo-resumo-realizacoes">
      <CabecalhoDaSecao
        id="titulo-resumo-realizacoes"
        titulo="Realizações Destaque"
        acao={
          <Link to={ROTA_DA_REALIZACAO_DESTAQUE} className={ACAO_DO_CABECALHO}>
            Acompanhar realizações
            <SetaDaAcao />
          </Link>
        }
      />
      <div className="rounded-2xl bg-brand-green px-6 py-6 lg:px-7">
        {props.aberto ? (
          <CicloAberto contadores={props.contadores} />
        ) : (
          <CicloFechado totalDoCiclo={props.totalDoCiclo} itens={props.itens} />
        )}
      </div>
    </section>
  );
}
